import { useRef, useState } from "react";

import { useImportRunRows, useImportRunning, useImportRuns, useRunImport } from "src/api/hooks";
import { ReadFailure } from "src/components/read-failure";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Empty,
  Field,
  Input,
  RadioGroup,
  type RadioOption,
  Table,
  humanPopulation
} from "src/components/ui";
import type { ImportRunFieldsFragment, Population, RunImportMutation } from "src/graphql/generated";
import { UNIMPORTED_OUTCOMES, humanOutcome, humanUnmatchedField, sheetHref } from "src/lib/import";
import type { Viewer } from "src/screens/types";

/**
 * IMPORT_HISTORY_LIMIT is how many past runs the history shows.
 *
 * Exported because the panel has to be able to say the number, and because one more
 * than it is what gets asked for — the same probe-row arrangement as the console
 * access history, for the same reason: a list that is truncated must say so, and one
 * that is complete must not.
 */
export const IMPORT_HISTORY_LIMIT = 20;

/**
 * IMPORT_ROW_PAGE is how much of one run's report is read back at a time.
 *
 * The server's own ceiling (`maxLimit` in internal/people/store.go), so asking for
 * more would return this anyway — and the ceiling does not lift, which is why the
 * report walks pages with an offset rather than asking for a bigger one. Before it
 * did, a run with more than this many unimported rows had failures nothing could reach:
 * not the console, and not the API underneath it.
 */
export const IMPORT_ROW_PAGE = 500;

/**
 * The two databases, as the picker offers them.
 *
 * Empty is a real state and not a placeholder: the form starts with neither chosen
 * and will not submit until one is. The sheet does not say which population it is —
 * the tab implies it — and a row whose subcategory belongs to the other one is
 * refused rather than moved, so a defaulted picker turns "I did not notice the
 * question" into an entire run of failures.
 */
type PopulationChoice = Population | "";

const populationOptions: readonly RadioOption<PopulationChoice>[] = [
  { value: "PARTNER", label: "Partners" },
  { value: "ON_DEMAND_CONTRACTOR", label: "On-demand contractors" }
];

const historyColumns = ["Started", "Ran by", "Database", "Mode", "Outcome", "Sheet", ""];

/**
 * The run whose report is on screen, and whatever the call that produced it said
 * beyond the stored record.
 *
 * `sheet`, `unmatched` and `ignoredColumns` are not stored anywhere: they ride on the
 * response of the run that produced them and nothing can read them back. So a run
 * selected out of the history has them absent rather than empty, and the report says
 * which of the two situations it is in — an empty unmatched list means every value
 * resolved, and claiming that about a run this session never saw would be an
 * invention.
 *
 * `notes` is on the response and is no longer only there: each one is stored on its
 * row in the report as well. The report reads whichever it has — see RunReport.
 */
type Selection = {
  run: ImportRunFieldsFragment;
  extra: Omit<RunImportMutation["runImport"], "run"> | null;
  fromHistory: boolean;
};

/**
 * The dry run that unlocks a real one, and the exact values it was performed against.
 *
 * The record is held here rather than read off whatever report happens to be on
 * screen, because those are two different things: opening an older run's report from
 * the history changes what is displayed and must not change what the confirmation is
 * quoting. The confirmation has to describe the run being approved.
 *
 * `revision` is the document half of the approval, and the reason the two are not the
 * same thing: `sourceUrl` and `population` say which request was approved, and the
 * revision says which version of the tab the numbers came from. It is sent back on the
 * real run and the server refuses the whole run if the tab has changed since.
 */
type Checked = {
  sourceUrl: string;
  population: Population;
  revision: string;
  dryRun: ImportRunFieldsFragment;
};

/**
 * Bulk import: read a Google Sheet the operator names, and write its rows.
 *
 * **iam-admin only, and not because it writes.** Matching is on the union of both
 * email columns across both populations, so a caller who could see only one of them
 * would create a second identity row for somebody already recorded in the other —
 * duplicating people it cannot see. That is why the permission is global
 * (`run_bulk_import`) rather than a population writer's, and the picker below does not
 * change it: choosing Partners still requires being able to read on-demand
 * contractors. The `wantGlobal` table in internal/auth/role_test.go is what holds
 * "only iam-admin" true.
 *
 * Hiding the tab is presentation, as everywhere else in this console. The store asks
 * for the permission on every call.
 */
export function BulkImport({ viewer }: { viewer: Viewer }) {
  const isAdmin = viewer.roles.includes("IAM_ADMIN");

  const [sourceUrl, setSourceUrl] = useState("");
  const [population, setPopulation] = useState<PopulationChoice>("");
  const [checked, setChecked] = useState<Checked | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);

  const run = useRunImport();
  // Not `run.isPending`: that is per-mount, and this screen unmounts on a tab change
  // while the request is still out.
  const running = useImportRunning();
  const history = useImportRuns(IMPORT_HISTORY_LIMIT + 1, isAdmin);

  if (!isAdmin) {
    return (
      <Card title="Bulk import">
        <p className="text-sm text-slate-600">
          Only iam-admins can import a sheet. Matching looks at both populations at once, so
          anything narrower would create a second record for somebody it cannot see.
        </p>
      </Card>
    );
  }

  const link = sourceUrl.trim();
  const ready = link !== "" && population !== "";
  // The real run is offered only for the exact values a dry run was just performed
  // against. Editing either one puts it back out of reach, because the report on
  // screen then describes a different request from the one the button would send.
  //
  // The document itself is checked by the server rather than here — both runs read the
  // sheet live, and only the side doing the reading can tell that the tab changed
  // between them. `expectedRevision` below is this screen's half of that.
  const dryRunStands =
    checked !== null && checked.sourceUrl === link && checked.population === population;

  const start = (dryRun: boolean) => {
    // Both buttons carry these as `disabled`, which is what actually stops a second
    // click — a disabled button dispatches no click at all. Repeated here because the
    // confirmation's own button has no disabled state, so on that path this is the
    // only lock there is.
    if (!ready || running) {
      return;
    }
    // The revision the approving dry run read, so the server can refuse a tab that has
    // been edited since. Read before the approval is spent just below, rather than
    // relying on the state update not having landed yet. A dry run sends none: it has
    // no earlier read to be held to, and it is the call that produces one.
    const expectedRevision = dryRun ? undefined : checked?.revision;
    if (!dryRun) {
      // Spent on dispatch rather than on success. A real run commits row by row, so
      // one that fails part-way has still written the rows it reached — and the
      // report that approved it stops describing the database at that moment. Leaving
      // it to `onSuccess`, which that path never reaches, re-enables the button the
      // moment the run stops.
      setChecked(null);
    }
    run.mutate(
      { sourceUrl: link, population: population as Population, dryRun, expectedRevision },
      {
        onSuccess: (data) => {
          const result = data.runImport;
          setSelection({
            run: result.run,
            fromHistory: false,
            extra: {
              sheet: result.sheet,
              revision: result.revision,
              unmatched: result.unmatched,
              ignoredColumns: result.ignoredColumns,
              notes: result.notes
            }
          });
          if (dryRun) {
            setChecked({
              sourceUrl: link,
              population: population as Population,
              revision: result.revision,
              dryRun: result.run
            });
          }
        }
      }
    );
  };

  const runs = history.data?.importRuns ?? [];
  const truncated = runs.length > IMPORT_HISTORY_LIMIT;
  const shownRuns = runs.slice(0, IMPORT_HISTORY_LIMIT);
  const selectedRun = selection?.run ?? null;

  return (
    <div className="flex flex-col gap-4">
      <Card title="Import a sheet">
        <div className="flex flex-col gap-4">
          {/* Sent exactly as typed, trimmed and no more. Nothing about the document
              is decided here — not the id, not the tab, not a column — so a link
              shape this console has never seen is the server's question to answer,
              and its message that explains it, rather than something a second copy
              of the rule refuses in the browser first. */}
          <Field
            label="Sheet link"
            hint="Paste the address from your browser. The tab it names is the tab that is read."
            required
          >
            <Input
              value={sourceUrl}
              disabled={running}
              placeholder="https://docs.google.com/spreadsheets/d/…/edit#gid=0"
              onChange={(e) => setSourceUrl(e.target.value)}
              className="w-full max-w-2xl"
            />
          </Field>

          <RadioGroup
            name="import-population"
            legend="Which database"
            value={population}
            options={populationOptions}
            disabled={running}
            onChange={setPopulation}
          />

          <EmailDeduplication />
          <SheetConventions />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={!ready || running}
              title={ready ? undefined : "Paste a link and choose a database first"}
              onClick={() => start(true)}
            >
              {running && run.variables?.dryRun ? "Dry running…" : "Dry run"}
            </Button>
            <Button
              variant="danger"
              disabled={!dryRunStands || running}
              title={
                dryRunStands
                  ? undefined
                  : "Dry run this exact link and database first — the report is what you are approving"
              }
              onClick={() => setConfirming(true)}
            >
              {running && run.variables?.dryRun === false ? "Importing…" : "Import for real"}
            </Button>
            <span className="max-w-2xl text-xs text-slate-500">
              A dry run does the whole import inside a transaction it then rolls back. It creates no
              people and changes no existing records; the report shows which rows would be created,
              skipped, or refused.
            </span>
          </div>

          {running ? (
            <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              A run is in progress. It is synchronous and a few hundred rows take a while. Switching
              tabs in this console does not stop it, but the report only reaches the screen that
              started it; closing the browser tab does stop it, and a real run leaves whatever it
              had already written written. Every run is recorded either way — find it in the history
              below.
            </p>
          ) : null}

          {/* The error toast is the only report a refused write gets for free, and it
              goes away. A run that failed is the one whose message an operator needs
              to read twice, so it also stays here until the next attempt. The message
              is the backend's, verbatim: it names the column, the row or the part of
              the link that was wrong. */}
          {!running && run.error ? (
            <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
              {run.error.message} — the run is in the history below, with what it reached.
            </p>
          ) : null}
        </div>
      </Card>

      {/* Mounted only with the dry run it is confirming, so the counts it quotes are
          that run's rather than whichever report the operator last opened. */}
      {confirming && checked ? (
        <ConfirmDialog
          open
          title="Import for real"
          confirmLabel="Import"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            start(false);
          }}
        >
          <p>
            This writes to the {humanPopulation(checked.population).toLowerCase()} database. A real
            run commits row by row, so stopping it part-way does not undo what it has already
            written.
          </p>
          <p className="mt-2">
            The dry run read {checked.dryRun.counts.rowsRead}{" "}
            {checked.dryRun.counts.rowsRead === 1 ? "row" : "rows"}: {checked.dryRun.counts.created}{" "}
            to create, {checked.dryRun.counts.skipped} skipped, {checked.dryRun.counts.failed}{" "}
            failed.
            {checked.dryRun.counts.attached > 0
              ? ` ${checked.dryRun.counts.attached} to attach.`
              : null}
          </p>
          {/* The fifth number, and the one that is work rather than an outcome. It is
              not part of the partition above — a noted row imported — so it is said
              separately, here, where the size of the reconciliation is still a reason
              to stop and look rather than something to discover afterwards. */}
          {checked.dryRun.counts.noted > 0 ? (
            <p className="mt-2">
              {checked.dryRun.counts.noted} of those{" "}
              {checked.dryRun.counts.noted === 1 ? "rows is" : "rows are"} listed under{" "}
              <em>Worth checking</em>. Read those notes on the dry-run report before importing.
            </p>
          ) : null}
          {/* What the numbers are worth, said rather than implied. The tab is read
              again, and the dry run's revision goes with the request, so a document
              edited in between is refused whole instead of imported under an approval
              that described something else (INFRA-717). The operator is told which of
              the two happens, because "refused, dry run it again" is a different thing
              to expect from "imported as it now stands". */}
          <p className="mt-2">
            The tab is read again now and held to the version this dry run read. If anything in it
            has changed since — including in another tab of your own browser — the run is refused
            before it writes anything and you dry run it again.
          </p>
        </ConfirmDialog>
      ) : null}

      {selectedRun && !selection?.fromHistory ? (
        // Keyed on the run, so opening another report starts on its first page rather
        // than on whichever page of the last one was being read.
        <RunReport
          key={selectedRun.id}
          run={selectedRun}
          extra={selection?.extra ?? null}
          onClose={() => setSelection(null)}
        />
      ) : null}

      <Card title="Import history">
        {history.isPending ? <Empty>Loading…</Empty> : null}
        {history.error ? <ReadFailure error={history.error} /> : null}
        {!history.isPending && !history.error && shownRuns.length === 0 ? (
          <Empty>No import has been run yet.</Empty>
        ) : null}
        {shownRuns.length > 0 ? (
          <Table head={historyColumns}>
            {shownRuns.map((r) => (
              <HistoryRow
                key={r.id}
                run={r}
                report={selection?.fromHistory && r.id === selectedRun?.id ? selection : null}
                onToggle={() =>
                  setSelection((current) =>
                    current?.run.id === r.id
                      ? current.fromHistory
                        ? null
                        : { ...current, fromHistory: true }
                      : { run: r, extra: null, fromHistory: true }
                  )
                }
              />
            ))}
          </Table>
        ) : null}
        {truncated ? (
          <p className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            Showing the {IMPORT_HISTORY_LIMIT} most recent runs. There are older ones, which this
            screen does not show.
          </p>
        ) : null}
        <p className="mt-3 text-xs text-slate-500">
          Every run, dry or real, whether it finished or not. This is what a run did; what it{" "}
          <em>changed</em> is in each person's own history, alongside every other edit to that
          record.
        </p>
      </Card>
    </div>
  );
}

/** The create-or-skip rule applies before any other values on an existing row are read. */
function EmailDeduplication() {
  return (
    <p className="max-w-2xl rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
      <strong className="font-semibold">Existing email means skip.</strong> Work email is checked
      first, then personal email, against both saved email fields. If either already exists, the
      entire row is skipped: no person details are changed and no engagements are added. Only new
      people are created. If rows share an email, the first successfully created row wins. If an
      email cell lists multiple addresses separated by commas, semicolons, or line breaks, only the
      first address is used.
    </p>
  );
}

/**
 * What the tab has to look like, stated before a run rather than discovered from a
 * failure.
 *
 * Each of these is a rule the importer cannot resolve for the operator, and the
 * failure each produces is either the whole run or — for the semicolon — silent. The
 * separator is the one that has to be here: `former_names` splits on `;` with no
 * escape, so "Kim; nee Park" is stored as two names and nothing detects it. There is
 * no escape this side of the wire can invent either, because a doubled or trailing
 * separator is ordinary punctuation in cells already in the document. So it is said
 * where somebody decides what to type. `formerNameList` in internal/importer/columns.go
 * points back here.
 */
function SheetConventions() {
  return (
    <details className="max-w-2xl rounded border border-slate-200 bg-slate-50 px-3 py-2">
      <summary className="cursor-pointer text-xs font-semibold text-slate-700">
        What the tab has to look like
      </summary>
      <ul className="mt-2 flex list-disc flex-col gap-1 pl-4 text-xs text-slate-600">
        <li>
          The first row with anything in it is read as the header. A title or banner line above it
          becomes the header, and the run fails naming columns you can see — delete that row, or
          clear every cell in it. Rows that are entirely blank are skipped.
        </li>
        <li>
          A heading this import reads may appear only once. Two columns headed{" "}
          <code>work_email</code> are refused rather than guessed between.
        </li>
        <li>
          New partner rows need a <code>DRI</code> column with Bosco, Jaeseung, Jingyu, Sunghyun, or
          Will. Existing-email rows are still skipped without changing their DRI. On-demand
          contractors do not use this field.
        </li>
        <li>
          <code>former_names</code> holds several names separated by semicolons, most recent first —
          and there is no escape. A name that itself contains a semicolon, like{" "}
          <code>Kim; nee Park</code>, is stored as two names and nothing will flag it. Record that
          one by hand from the person&rsquo;s own screen instead.
        </li>
        <li>
          Blank, <code>-</code> and <code>n/a</code> all mean &ldquo;not recorded&rdquo;. In{" "}
          <code>end_date</code>, <code>Current</code> and its synonyms mean the term has no last
          day; in <code>start_date</code> they are refused, because a start date has no open end to
          describe.
        </li>
      </ul>
    </details>
  );
}

function HistoryRow({
  run,
  report,
  onToggle
}: {
  run: ImportRunFieldsFragment;
  report: Selection | null;
  onToggle: () => void;
}) {
  const href = sheetHref(run.sourceUrl);
  const label = run.sheetTitle ?? run.sourceUrl;
  const reportButton = useRef<HTMLButtonElement>(null);
  const reportId = `import-report-${run.id}`;

  return (
    <>
      <tr className="border-b border-slate-100 align-top">
        <td className="px-2 py-2 whitespace-nowrap">{new Date(run.startedAt).toLocaleString()}</td>
        <td className="px-2 py-2">{run.actorEmail}</td>
        <td className="px-2 py-2">{humanPopulation(run.population)}</td>
        <td className="px-2 py-2">
          {run.dryRun ? <Badge tone="amber">Dry run</Badge> : <Badge>Real</Badge>}
        </td>
        <td className="px-2 py-2">
          <div className="flex flex-col gap-1">
            <span>
              <RunStatus run={run} />
            </span>
            <span className="text-xs text-slate-500">
              {run.counts.rowsRead} read · {run.counts.created + run.counts.attached} in ·{" "}
              {run.counts.skipped} skipped · {run.counts.failed} failed
            </span>
            {run.failureReason ? (
              <span className="text-xs text-red-700">{run.failureReason}</span>
            ) : null}
          </div>
        </td>
        <td className="px-2 py-2">
          {/* A link only when the stored text is one. `sourceUrl` is whatever was
            pasted, including on the runs that failed because it was wrong.

            The tab's title reads better than a 90-character URL, so it is the label
            wherever the run got far enough to resolve one — which means the visible
            text and the destination are not the same string, and the title attribute
            is what lets a reader see where this goes before following it. */}
          {href ? (
            <a
              className="text-sm text-slate-700 underline"
              href={href}
              title={run.sourceUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {label}
            </a>
          ) : (
            <span className="text-sm text-slate-700">{label}</span>
          )}
        </td>
        <td className="px-2 py-2">
          <Button
            ref={reportButton}
            variant="secondary"
            aria-expanded={report !== null}
            aria-controls={report ? reportId : undefined}
            onClick={onToggle}
          >
            {report ? "Hide report" : "Report"}
          </Button>
        </td>
      </tr>
      {report ? (
        <tr className="border-b border-slate-100">
          <td colSpan={historyColumns.length} className="bg-slate-50 px-2 py-3">
            <div id={reportId}>
              <RunReport
                run={report.run}
                extra={report.extra}
                onClose={() => {
                  onToggle();
                  reportButton.current?.focus();
                }}
              />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RunStatus({ run }: { run: ImportRunFieldsFragment }) {
  switch (run.status) {
    case "COMPLETED":
      return <Badge tone="green">Completed</Badge>;
    case "FAILED":
      return <Badge tone="red">Failed</Badge>;
    default:
      return <Badge tone="amber">Running</Badge>;
  }
}

/**
 * One run's report: what it did, what it could not resolve, and which rows are not in
 * the database.
 *
 * The same component for a run that just finished and for one picked out of the
 * history, which is what makes the mid-run advice above honest: losing the response
 * costs the three unstored fields and nothing else.
 */
function RunReport({
  run,
  extra,
  onClose
}: {
  run: ImportRunFieldsFragment;
  extra: Omit<RunImportMutation["runImport"], "run"> | null;
  onClose: () => void;
}) {
  const [page, setPage] = useState(0);
  // Narrowed to the two unimported outcomes by the server, which is what makes a page
  // of this a page of failures. A page of the whole report of a large sheet can carry
  // none at all, and walking forty pages to find three is not a report.
  const rows = useImportRunRows(
    run.id,
    UNIMPORTED_OUTCOMES,
    false,
    IMPORT_ROW_PAGE,
    page * IMPORT_ROW_PAGE
  );

  const notImported = rows.data?.importRunRows ?? [];
  // A second query, because the list above asks the server for the two unimported
  // outcomes and a note rides on a row of any outcome — the attaches, which the failure
  // filter excludes, are the ones most likely to carry one. Asking for the noted rows
  // is what makes them reachable on a sheet whose report runs to thousands of rows.
  const notedRows = useImportRunRows(run.id, [], true, IMPORT_ROW_PAGE, 0);
  // Two sources for one section, because each is better at a different thing. The
  // response carries every note the run produced, unwindowed — so a run this session
  // made shows the complete set. The stored rows carry the same text on the row it
  // belongs to — so a run opened out of the history shows them at all, which before
  // INFRA-720 it could not: notes lived only on a reply nobody kept, and a note says a
  // row left something in the sheet unwritten.
  const noted: { key: string; sheetRow: number; note: string }[] = extra
    ? extra.notes.map((n) => ({
        key: `${n.sheetRow}-${n.note}`,
        sheetRow: n.sheetRow,
        note: n.note
      }))
    : (notedRows.data?.importRunRows ?? []).map((row) => ({
        key: row.id,
        sheetRow: row.sheetRow,
        note: row.note as string
      }));
  // Nothing below may describe the report until the report is here. `notImported` is
  // empty while the query is in flight or has failed, which is not the same fact as
  // a run that had nothing to report.
  const loaded = !rows.isPending && !rows.error;
  // `skipped + failed` is the non-imported half of a partition rather than two of
  // four loose numbers, which is what makes it the size of the list being paged:
  // TestTheOutcomesPartitionTheRowsRead in internal/people holds every row to exactly
  // one outcome, and UNIMPORTED_OUTCOMES is derived from the same Record the server's
  // two values are, so the total and the query cannot come to disagree about which
  // rows they mean.
  const countedTotal = run.counts.skipped + run.counts.failed;
  // But the counts are a snapshot. They ride on the run record the history handed us,
  // and a run still in progress appends rows after that snapshot was taken — so a page
  // that came back *full* is the one thing that proves there may be another, whatever
  // the counts say. Trusting them alone left Next disabled with rows still to come,
  // which is the trap this whole pager exists to remove. Found in review by Devin.
  const pageIsFull = notImported.length >= IMPORT_ROW_PAGE;
  const seenSoFar = page * IMPORT_ROW_PAGE + notImported.length;
  const notImportedTotal = Math.max(countedTotal, seenSoFar);
  const pageCount = Math.max(
    Math.ceil(notImportedTotal / IMPORT_ROW_PAGE),
    page + 1 + (pageIsFull ? 1 : 0),
    1
  );
  const firstOnPage = page * IMPORT_ROW_PAGE + 1;
  const lastOnPage = seenSoFar;
  const everythingImported = loaded && notImportedTotal === 0;
  // How many notes the run has, as against how many are on screen. The response, where
  // there is one, carries every note the run produced and is not a page; the stored
  // rows are, so there the run's own count is the total and the difference is what the
  // page could not carry. Without this a note on row 550 of a 600-row run is simply
  // absent, under a heading saying every row imported — the state that section exists
  // to end.
  const notedTotal = extra ? noted.length : Math.max(run.counts.noted, noted.length);
  const unlistedNotes = notedRows.isPending || notedRows.error ? 0 : notedTotal - noted.length;

  return (
    <Card
      title={run.dryRun ? "Dry run report" : "Import report"}
      actions={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* Real mode permits a write; it does not perform one. A run that failed on
              the link or the header row reached no row at all, and one that failed
              every row wrote nothing either — so the badge reads off what actually
              landed rather than off the mode. `created + attached` is the imported
              half of the same partition the shortfall below subtracts from. */}
          {run.dryRun ? (
            <Badge tone="amber">Dry run — nothing was written</Badge>
          ) : run.counts.created + run.counts.attached > 0 ? (
            <Badge tone="green">Written to the database</Badge>
          ) : (
            <Badge>Real run — no rows were written</Badge>
          )}
          <RunStatus run={run} />
          <span className="text-sm text-slate-600">{humanPopulation(run.population)}</span>
        </div>

        <div className="text-xs text-slate-600">
          Actor IP: <span className="font-mono">{run.actorIp ?? "Not recorded"}</span>
        </div>

        {run.failureReason ? (
          <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
            {run.failureReason}
          </p>
        ) : null}

        {/* Which tab was read, in the run's own words — including whether it fell
            back to the leftmost one because the link named none. Only the operator
            can tell whether that was the tab they meant. */}
        {extra ? <p className="text-sm text-slate-700">Read {extra.sheet}</p> : null}

        <dl className="flex flex-wrap gap-6">
          <Count label="Rows read" value={run.counts.rowsRead} />
          <Count label={run.dryRun ? "Would create" : "Created"} value={run.counts.created} />
          {run.counts.attached > 0 ? (
            <Count label={run.dryRun ? "Would attach" : "Attached"} value={run.counts.attached} />
          ) : null}
          <Count label="Skipped" value={run.counts.skipped} />
          <Count label="Failed" value={run.counts.failed} />
        </dl>
        <p className="text-xs text-slate-500">
          Each row has one outcome. Skipped rows make no changes to existing people.
          {run.counts.attached > 0
            ? " This older run also attached terms to existing people; new imports no longer do that."
            : null}
        </p>

        {extra ? (
          <>
            <UnmatchedValues unmatched={extra.unmatched} />
            <IgnoredColumns columns={extra.ignoredColumns} />
          </>
        ) : (
          <p className="text-xs text-slate-500">
            Unresolved values and ignored columns are not stored — they were on the response of the
            run that produced them. This report is the stored record, so it has the verdicts, the
            counts and the row notes, but not the values nothing matched or the columns nothing
            read.
          </p>
        )}

        {noted.length > 0 || unlistedNotes > 0 ? (
          <div>
            <h3 className="mb-1 text-sm font-semibold text-slate-800">
              Worth checking ({notedTotal})
            </h3>
            <p className="mb-2 text-xs text-slate-500">
              These notes describe something to check in the sheet or imported record, such as a
              legal_full_name that differs from its name parts. Notes are stored with each row and
              remain available in historical reports.
            </p>
            <ul className="flex flex-col gap-1 text-sm text-slate-700">
              {noted.map((row) => (
                <li key={row.key}>
                  Row {row.sheetRow}: {row.note}
                </li>
              ))}
            </ul>
            {unlistedNotes > 0 ? (
              <p className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {unlistedNotes} more {unlistedNotes === 1 ? "note is" : "notes are"} not listed
                here: notes are read back {IMPORT_ROW_PAGE} rows at a time. The count is the
                run&rsquo;s own and covers every row.
              </p>
            ) : null}
          </div>
        ) : null}

        <div>
          <h3 className="mb-1 text-sm font-semibold text-slate-800">Rows that did not import</h3>
          <p className="mb-2 text-xs text-slate-500">
            Numbered as the sheet numbers them, so a row can be found in the tab and fixed.
          </p>
          {rows.isPending ? <Empty>Loading…</Empty> : null}
          {rows.error ? <ReadFailure error={rows.error} /> : null}
          {everythingImported ? (
            <Empty>
              {run.counts.rowsRead === 0
                ? "The run read no rows."
                : `All ${run.counts.rowsRead} rows ${run.dryRun ? "would import" : "imported"}.`}
            </Empty>
          ) : null}
          {notImported.length > 0 ? (
            <Table head={["Sheet row", "Outcome", "Reason"]}>
              {notImported.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 align-top">
                  <td className="px-2 py-2 whitespace-nowrap">{row.sheetRow}</td>
                  <td className="px-2 py-2">
                    <Badge tone={row.outcome === "FAILED" ? "red" : "slate"}>
                      {humanOutcome(row.outcome)}
                    </Badge>
                  </td>
                  <td className="px-2 py-2 text-slate-700">{row.failureReason}</td>
                </tr>
              ))}
            </Table>
          ) : null}
          {/* The pager only appears where there is more than one page, and it is exact
              rather than a "show more": `notImportedTotal` is the server's own count of
              the very rows this list holds, so the position can be stated instead of
              guessed at. Before this the screen said the report was read back 500 rows
              "at a time" and there was no next page — rows past the first 500 could not
              be reached through the console or the API.

              Rendered while the next page is in flight, with its buttons disabled,
              rather than gated on `loaded`: a pager that vanished on every click and
              came back is worse than one that waits. Which is also why the row range is
              stated only once there are rows to range over — an empty page would
              otherwise read "Rows 501–500". */}
          {pageCount > 1 ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                disabled={page === 0 || !loaded}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                disabled={page >= pageCount - 1 || !loaded}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
              <span className="text-xs text-slate-600">
                {notImported.length > 0
                  ? `Rows ${firstOnPage}–${lastOnPage} of ${notImportedTotal} that did not import. Page ${page + 1} of ${pageCount}.`
                  : `Page ${page + 1} of ${pageCount}, of ${notImportedTotal} rows that did not import.`}
              </span>
            </div>
          ) : null}
          {/* A page that came back empty while the run says there are rows to show. The
              counts are aggregated live, so a run still in progress can grow past the
              page an operator is on; going back to the first page is the move. */}
          {loaded && notImported.length === 0 && notImportedTotal > 0 ? (
            <p className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              This page of the report is empty, but the run counts {notImportedTotal}{" "}
              {notImportedTotal === 1 ? "row" : "rows"} that did not import.{" "}
              {page > 0 ? (
                <button type="button" className="underline" onClick={() => setPage(0)}>
                  Go back to the first page
                </button>
              ) : (
                "Reload the report."
              )}
            </p>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/**
 * Distinct values no lookup answered to, each once with the number of rows naming it.
 *
 * The whole reason this is a section of its own: one firm spelled wrong on forty rows
 * is one thing to fix, and reading it as forty row failures is how an operator
 * concludes the sheet is hopeless. The grouping is the server's — every row still
 * appears below with its own reason.
 */
function UnmatchedValues({
  unmatched
}: {
  unmatched: RunImportMutation["runImport"]["unmatched"];
}) {
  if (unmatched.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        No unresolved entity or subcategory values were reported. New partner organizations are
        created automatically.
      </p>
    );
  }
  const rowsNaming = unmatched.reduce((total, value) => total + value.rows, 0);

  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-slate-800">
        Values nothing matched ({unmatched.length})
      </h3>
      <p className="mb-2 text-xs text-slate-500">
        Named on {rowsNaming} {rowsNaming === 1 ? "row" : "rows"} between them. Correct these values
        before running again. Missing partner organizations are created automatically for new
        people.
      </p>
      <Table head={["Where", "Value", "Rows"]}>
        {unmatched.map((value) => (
          <tr key={`${value.field}-${value.value}`} className="border-b border-slate-100">
            <td className="px-2 py-2">{humanUnmatchedField(value.field)}</td>
            <td className="px-2 py-2 text-slate-700">{value.value}</td>
            <td className="px-2 py-2">{value.rows}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

/**
 * The headings the tab carried that this import did not read.
 *
 * Said on every run, because the alternative is silence. Which columns this database
 * declines to record is a decision with reasons — it holds who is engaged and on what
 * terms, and the source documents, the target-system link and the inputter belong to
 * the systems that own them — but an operator reading the result of their own run is
 * not reading the README, and a column dropped without a word is indistinguishable
 * from one that was missed. It is also the only thing that will say anything when the
 * sheet grows a column somebody expects to be imported.
 */
function IgnoredColumns({
  columns
}: {
  columns: RunImportMutation["runImport"]["ignoredColumns"];
}) {
  if (columns.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        Every column in the tab was read. Nothing was ignored.
      </p>
    );
  }

  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-slate-800">
        Columns this import ignored ({columns.length})
      </h3>
      <p className="mb-2 text-xs text-slate-500">
        Read past, with nothing recorded from them. This database holds who is engaged and on what
        terms; citizenship, the source documents, the target-system link and the inputter belong to
        the systems that own them. If a column here is one you expected to be imported, it has not
        been — say so rather than running again.
      </p>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-700">
        {columns.map((column) => (
          <li key={column} className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs">
            {column}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-lg font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
